/**
 * Pure display helpers for the Mijn-VNL order overview and detail pages.
 *
 * The order overview lists every ordered item type (e-learnings, digital
 * tools, physical trainings and physical products). License management
 * (invitations) is e-learning-only, so these helpers centralise the
 * "is this an e-learning?" / "does this order have license management?"
 * decisions used by OrderCard and OrderDetail.
 */
import type { Order, ProductType } from '../types/orders';

/**
 * Whether the order is an e-learning order.
 *
 * @param productType - The product type machine name from the orders API.
 * @returns True for e-learning, false for every other (or unknown) type.
 */
export const isElearningOrder = (productType: ProductType | null): boolean => productType === 'e_learning';

/**
 * Whether the "Beheer licenties" (manage licenses) affordance applies.
 *
 * Only e-learning orders carry invitations, and only when they cover more
 * than one seat or were ordered for someone else. Trainings, physical
 * products and digital tools never show license management — even at
 * quantities greater than one.
 *
 * @param order - The slim order object from the overview list.
 * @returns True when the manage-licenses button/link should be shown.
 */
export const orderHasLicenseManagement = (order: Order): boolean => {
  if (!isElearningOrder(order.product_type) || !order.invite_stats) {
    return false;
  }
  return order.someone_else || order.quantity > 1;
};
